import type { DepartmentScene } from 'app/views/home/webgl/webgl.types';

/**
 * No-op scaffold for the per-department field scenes (the departments/
 * classes). Every new department scene — e.g. the future shaders type's —
 * extends this and overrides only the hooks it cares about; everything else
 * inherits a safe no-op body, so HomeField / DepartmentSwitcher can call the
 * full DepartmentScene surface uniformly without existence or identity
 * guards.
 *
 * The class is deliberately concrete (not abstract): a scene that does
 * nothing is a valid DepartmentScene, so an instance can serve directly as a
 * null-object scene where a placeholder is needed.
 */
export default class BaseDepartmentScene implements DepartmentScene {
	/**
	 * Per-frame update. HomeField.tick() calls this on the single active scene
	 * every frame (SystemsDepartment is additionally ticked while inactive —
	 * a HomeField special case, not part of this contract).
	 */
	public tick(): void {}

	/**
	 * Called when this scene becomes the active one: by
	 * HomeField.setActiveSceneProcessor (on a short delay, after the outgoing
	 * scene's stopAsActiveScene) when DepartmentSwitcher detects drag progress
	 * crossing into this scene's zone, and directly on intro/main-scene start.
	 * `reposition` lets callers skip the initial particle re-layout (used by
	 * the from-work re-entry).
	 */
	public startAsActiveScene(reposition?: boolean): void {}

	/**
	 * Called on the outgoing scene by HomeField.setActiveSceneProcessor just
	 * before the incoming scene starts. Park particles / hide the scene's
	 * sub-container here.
	 */
	public stopAsActiveScene(): void {}

	/** User started dragging while this scene is active. */
	public startSceneDrag(): void {}

	/** User released a drag while this scene is active. */
	public endSceneDrag(): void {}

	/**
	 * Window resize while this scene is active: HomeField.resizeEvent forwards
	 * the resize to the active scene only.
	 */
	public resize(): void {}

	/**
	 * Hide / park everything ahead of moving to the work section.
	 * Department-routed by HomeField.setupForWork.
	 */
	public setupForWork(): void {}

	/**
	 * Re-enable and restart the scene when returning from the work section.
	 * Department-routed by HomeField.showFromWork.
	 */
	public showFromWork(): void {}

	/**
	 * Animate the scene's container back in on the work-to-home transition
	 * (counterpart of the toWork() animations).
	 */
	public fromWorkToHome(): void {}
}
